import { takeEvery } from 'redux-saga/effects';
import { io } from 'socket.io-client';
import { isNil } from 'lodash-es';

import store from '../index';

import { fetchSources } from '../slices/dataSources';
import { setLogo } from '../slices/ml-services.ts';
import {
  clearSessionData,
  sessionDeploy,
  sessionLoad,
  SessionStatus,
  setSessionProgress,
  updateSessionPostPayment,
  updateSessionInvoice,
} from '../slices/session';
import { fetchConfig } from '../slices/templatesConfig';
import { upsertJob, setStreaming, fetchCreativeJobs } from '../slices/creativeJobs';
import { fetchOwnerNotifications, ownerNotificationArrived } from '../slices/ownerNotifications';
import { isFeatureEnabled } from '../../nova/config/featureFlags';
import * as actionTypes from '../constants';

let socket = null;

const executeWithDelay = (fn) => {
  setTimeout(fn, Math.floor(Math.random() * 3000));
};

export const connectPlayerWebsocket = () => {
  if (!isNil(socket)) {
    return;
  }

  const state = store.getState();

  //console.log("player ws to ", import.meta.env.CFW, window.config.playerWsUrl + window.config.playerWsPath);

  const query = {
    protocol: 'sio1',
  };

  if (window.config && window.config.clientId) {
    query['tenantId'] = window.config.clientId;
  }

  const options = {
    path: window.config.playerWsPath,
    forceNew: true,
    transports: ['websocket'],
    allowEIO3: true,
    query,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    timeout: 20000,
  };

  if (state.authentication.access_token) {
    options.query.auth = state.authentication.access_token;
    options.auth = {
      token: state.authentication.access_token,
    };
  }

  socket = io(window.config.playerWsUrl, options);

  window.addEventListener('offline', () => {
    if (!isNil(socket) && socket.connected) {
      console.log('CONNECTION OFFLINE, SOCKET DISCONNECTING ...');

      socket.disconnect();
    }
  });

  window.addEventListener('online', () => {
    if (!isNil(socket) && socket.disconnected) {
      console.log('CONNECTION ONLINE, SOCKET RECONNECTING ...');

      socket.connect();
    }
  });

  socket.on('templatization_config_change', (data) => {
    try {
      if (data && data.length && Array.isArray(data)) {
        const tenantId = window.config.clientId;
        const siteId = window.config.siteId;

        const exists = data.find((item) => {
          if (typeof item === 'object') {
            return (
              item?.tenantId?.toString?.() === tenantId?.toString?.() &&
              item?.siteId?.toString?.() === siteId?.toString?.()
            );
          } else if (typeof item === 'string') {
            return item?.toString?.() === siteId?.toString?.();
          }
          return false;
        });
        if (exists) {
          if (import.meta.env.DEV) {
            // for dev execute immediately
            store.dispatch(fetchConfig({ tenantId: tenantId, siteId: siteId, bucketPath: exists?.bucketPath }));
          } else {
            executeWithDelay(() => {
              store.dispatch(
                fetchConfig({ tenantId: tenantId, siteId: window.config.siteId, bucketPath: exists?.bucketPath }),
              );
            });
          }
        }
      }
    } catch (e) {
      /**/
    }
  });

  socket.on('data_source_change', (data) => {
    if (data && data.length && Array.isArray(data)) {
      const state = store.getState().dataSources.items;

      let fetchIds = [];

      // re-fetch only the data sources that are already in the state; the rest will get fetched on demand
      data.forEach((id) => {
        if (id === window.config.dataSourceAllJackpots) return;
        if (state[id] != null) {
          fetchIds.push(id);
        }
      });

      if (fetchIds.length) {
        executeWithDelay(() => {
          store.dispatch(fetchSources({ ids: fetchIds }));
        });
      }
    }
  });

  socket.on('art_logo_generated', (data) => {
    // The generate request carries `remove_background: true`, so the delivered
    // asset is already the transparent cutout — straight to the grid.
    store.dispatch(setLogo(data));
  });

  // `{ invoice_id, invoice_status }` — pushed whenever a payment lands on the session's
  // invoice. This is the settlement signal for the ASYNCHRONOUS rails: a SPEI bank transfer
  // clears minutes to a business day after the buyer has left the checkout, so unlike a card
  // charge there is no in-request confirmation to react to. `invoice_status === 'paid'` is the
  // point the brand can be deployed.
  socket.on('invoice_update', (data) => {
    store.dispatch(updateSessionInvoice(data));
  });

  socket.on('session_update', (data) => {
    // New logic to redirect and block user to a new page
    if (data && data.status === SessionStatus.payment_successful) {
      store.dispatch(
        updateSessionPostPayment({
          ...data,
          postPayment: data.email_verification_required ? 'success-mail' : 'success-redirect',
        }),
      );
    }

    // Might not be needed anymore since the payment_successful case was added
    if (data && data.status === SessionStatus.customization) {
      store.dispatch(
        updateSessionPostPayment({
          ...data,
          postPayment: state.authentication.user.user_id === data.user_id ? 'redirect' : 'mail',
        }),
      );
    }

    if (data && data.status === SessionStatus.deployment_in_progress) {
      store.dispatch(setSessionProgress(data));
    }

    if (data && data.status === SessionStatus.done) {
      store.dispatch(setSessionProgress(data));
      store.dispatch(clearSessionData());
    }
  });

  // Creatives generation jobs. The Studio's realtime feed moved OFF the (now-removed)
  // spark-creatives SSE stream onto THIS owner-hub socket: spark-creatives POSTs each job state
  // change to owner-hub's /internal/ws/send as a `creative_job` event targeted at the owner, so
  // every tab the owner has open receives it. Payload is the jobs.Update shape the creativeJobs
  // slice already merges by job_id (see spark-creatives docs/admin-api.md).
  socket.on('creative_job', (data) => {
    // Guard the dispatch: a malformed/empty frame must not throw out of the socket.io listener
    // (the removed SSE handler wrapped its parse + dispatch the same way).
    try {
      store.dispatch(upsertJob(data));
    } catch (e) {
      /* ignore a malformed creative_job frame */
    }
  });

  // A notification Prisma sent the owner (owner-hub docs/owner-notifications.md): into the bell of every tab, and
  // announced by the bell (it rings, never a pop-up) only in the tab the owner is looking at (the others find it
  // shown on their return), never for a support agent impersonating the owner (the announcement is the owner's).
  // Ignored while the `owner-notifications` flag is off.
  socket.on('owner_notification', (data) => {
    if (!isFeatureEnabled('owner-notifications')) return;
    try {
      const announce = !store.getState().authentication?.user?.admin && document.visibilityState === 'visible';
      store.dispatch(ownerNotificationArrived({ notification: data?.notification, announce }));
    } catch (e) {
      /* ignore a malformed owner_notification frame */
    }
  });

  // The durable source of truth is GET /creatives/jobs. Rehydrate it on (re)connect so a run that
  // started while this socket was down (or before it came up) still lands in the grid; live
  // `creative_job` events then apply on top (newest state wins). Pushes are best-effort, so the
  // rehydrate (not the stream) is what guarantees the grid is correct after any gap. This socket is
  // SHARED with the guest/configurator flow, so gate on a signed-in owner: only they have creative
  // jobs, and an anonymous session must not fire an unauthenticated GET at the creatives backend.
  socket.on('connect', () => {
    if (!store.getState().authentication.access_token) return;
    store.dispatch(setStreaming(true));
    store.dispatch(fetchCreativeJobs());
    // A notification pushed while the socket was down is read here (behind its flag).
    if (isFeatureEnabled('owner-notifications')) store.dispatch(fetchOwnerNotifications());
  });

  socket.on('disconnect', () => {
    store.dispatch(setStreaming(false));
  });
};

export function* websocketPlayerLoginSaga(action) {
  if (isNil(socket)) {
    connectPlayerWebsocket();
    return;
  }

  if (['user', 'token'].indexOf(action.authType) > -1) {
    yield socket.emit('login', {
      token: 'Bearer ' + window.config.guestToken,
    });
  }
}

export function websocketPlayerLogoutSaga() {
  if (isNil(socket)) {
    return;
  }

  socket.emit('logout');
}

export function websocketSetSession() {
  if (isNil(socket)) {
    return;
  }

  const state = store.getState();
  const { session } = state;

  if (isNil(session.sessionId)) {
    return;
  }

  socket.emit('set_session', { session_id: session.sessionId });
}

export function* watchPlayerWebsocket() {
  yield takeEvery(actionTypes.profile.RECEIVED_ACCOUNT, websocketPlayerLoginSaga);
  yield takeEvery(actionTypes.authentication.CLEAR, websocketPlayerLogoutSaga);
  yield takeEvery(sessionLoad.fulfilled, websocketPlayerLoginSaga);
  yield takeEvery(sessionLoad.fulfilled, websocketSetSession);
  yield takeEvery(sessionDeploy.pending, websocketSetSession);
}
